import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { SeverityBadge } from '@/components/shared/SeverityBadge'
import {
  useAcceptAutomatedField,
  useAdminVulnerabilities,
  useAdminVulnerability,
  useUpdateVulnerability,
  type AdminVulnerability,
  type VulnerabilityExploitationStatus,
} from '@/hooks/useAdminVulnerabilities'
import { formatDate } from '@/lib/utils'
import { isV5AdvisoriesEnabled } from '@/lib/flags'
import { VendorAdvisoryAdminSection } from '@/components/admin/VendorAdvisoryAdminSection'

const EXPLOIT_STATUSES: VulnerabilityExploitationStatus[] = [
  'none',
  'poc',
  'active',
  'widespread',
]

/** V2: render KEV lifecycle dates as YYYY-MM-DD UTC for the provenance line. */
function formatKevDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

interface FormState {
  exploitationStatus: VulnerabilityExploitationStatus
  patchAvailable: boolean
  adminNotes: string
  // V3 — classifier-override fields. Stored as empty-string-for-null; the
  // diff builder coerces "" → null before sending the PATCH body so the
  // API's Zod schema can apply its empty→null transform.
  fixVersionHint: string
  primaryAdvisoryUrl: string
}

function toForm(v: AdminVulnerability): FormState {
  return {
    exploitationStatus: v.exploitationStatus,
    patchAvailable: !!v.patchAvailable,
    adminNotes: v.adminNotes ?? '',
    fixVersionHint: v.fixVersionHint ?? '',
    primaryAdvisoryUrl: v.primaryAdvisoryUrl ?? '',
  }
}

export function VulnerabilityEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useAdminVulnerability(id)
  const update = useUpdateVulnerability(id ?? '')
  const acceptExploit = useAcceptAutomatedField(id ?? '')
  const acceptPatch = useAcceptAutomatedField(id ?? '')

  const [form, setForm] = useState<FormState | null>(null)

  // Find the next "needs review" row — used by Save & next.
  const { data: listForNav } = useAdminVulnerabilities({ needs_review: true })

  useEffect(() => {
    if (data) setForm(toForm(data))
  }, [data])

  const hasChanges = useMemo(() => {
    if (!data || !form) return false
    return (
      form.exploitationStatus !== data.exploitationStatus ||
      form.patchAvailable !== !!data.patchAvailable ||
      form.adminNotes !== (data.adminNotes ?? '') ||
      form.fixVersionHint !== (data.fixVersionHint ?? '') ||
      form.primaryAdvisoryUrl !== (data.primaryAdvisoryUrl ?? '')
    )
  }, [data, form])

  if (isLoading || !form) {
    return (
      <main className="p-6">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-64 w-full" />
      </main>
    )
  }
  if (isError || !data) {
    return (
      <main className="p-6">
        <ErrorState
          title="Vulnerability not found"
          onRetry={() => refetch()}
        />
      </main>
    )
  }

  const diffPayload = () => {
    const payload: {
      exploitationStatus?: VulnerabilityExploitationStatus
      patchAvailable?: boolean
      adminNotes?: string
      fixVersionHint?: string | null
      primaryAdvisoryUrl?: string | null
    } = {}
    if (form.exploitationStatus !== data.exploitationStatus)
      payload.exploitationStatus = form.exploitationStatus
    if (form.patchAvailable !== !!data.patchAvailable)
      payload.patchAvailable = form.patchAvailable
    if (form.adminNotes !== (data.adminNotes ?? ''))
      payload.adminNotes = form.adminNotes
    if (form.fixVersionHint !== (data.fixVersionHint ?? '')) {
      // Empty input → clear the field (send null). Trimmed on the server too.
      payload.fixVersionHint = form.fixVersionHint.trim() || null
    }
    if (form.primaryAdvisoryUrl !== (data.primaryAdvisoryUrl ?? '')) {
      payload.primaryAdvisoryUrl = form.primaryAdvisoryUrl.trim() || null
    }
    return payload
  }

  const onSave = async () => {
    const payload = diffPayload()
    if (Object.keys(payload).length === 0) return
    await update.mutateAsync(payload)
  }

  const onSaveAndNext = async () => {
    const payload = diffPayload()
    if (Object.keys(payload).length > 0) {
      await update.mutateAsync(payload)
    }
    const nextId = (listForNav?.items ?? []).find(
      (v) => v.id !== id,
    )?.id
    if (nextId) {
      navigate(`/admin/vulnerabilities/${nextId}/edit`)
    } else {
      navigate('/admin/vulnerabilities')
    }
  }

  return (
    <main className="mx-auto flex max-w-4xl flex-col gap-6 p-4 md:p-6">
      <Link
        to="/admin/vulnerabilities"
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Back to vulnerabilities
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-mono text-2xl font-semibold">{data.cveId}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{data.title}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {data.severity && <SeverityBadge severity={data.severity} />}
            {data.cvssScore && (
              <Badge variant="outline">CVSS {data.cvssScore}</Badge>
            )}
            {data.epssScore && (
              <Badge variant="outline">
                EPSS {(parseFloat(data.epssScore) * 100).toFixed(1)}%
              </Badge>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Provenance cards */}
        <section className="flex flex-col gap-4">
          <div className="rounded-md border border-border p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <History className="h-4 w-4 text-muted-foreground" aria-hidden />
              Exploit status provenance
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {data.exploitationStatusChangedBy ? (
                <>
                  Source:{' '}
                  <span className="font-mono text-foreground">
                    {data.exploitationStatusChangedBy}
                  </span>
                  {data.exploitationStatusChangedAt &&
                    ` · ${formatDate(data.exploitationStatusChangedAt)}`}
                </>
              ) : (
                <>No source stamp (legacy or accepted automated).</>
              )}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() =>
                acceptExploit.mutateAsync({ field: 'exploitationStatus' })
              }
              disabled={acceptExploit.isPending}
              aria-label="Accept automated exploitation"
            >
              Accept automated (exploitation)
            </Button>
            {data.kevFirstSeenAt && (
              <p
                className="mt-3 text-xs text-muted-foreground"
                data-testid="kev-history-line"
              >
                KEV history: first seen {formatKevDate(data.kevFirstSeenAt)}
                {data.kevLastSeenAt &&
                  ` · last seen ${formatKevDate(data.kevLastSeenAt)}`}
                {data.kevRemovedAt &&
                  ` · removed ${formatKevDate(data.kevRemovedAt)}`}
              </p>
            )}
          </div>

          <div className="rounded-md border border-border p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <History className="h-4 w-4 text-muted-foreground" aria-hidden />
              Patch provenance
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {data.patchAvailableChangedBy ? (
                <>
                  Source:{' '}
                  <span className="font-mono text-foreground">
                    {data.patchAvailableChangedBy}
                  </span>
                  {data.patchAvailableChangedAt &&
                    ` · ${formatDate(data.patchAvailableChangedAt)}`}
                </>
              ) : (
                <>No source stamp (legacy or never set).</>
              )}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() =>
                acceptPatch.mutateAsync({ field: 'patchAvailable' })
              }
              disabled={acceptPatch.isPending}
              aria-label="Accept automated patch"
            >
              Accept automated (patch)
            </Button>
          </div>

          {data.updatedByEmail && (
            <p className="text-xs text-muted-foreground">
              Last updated by{' '}
              <span className="font-mono">{data.updatedByEmail}</span>
              {` · ${formatDate(data.updatedAt)}`}
            </p>
          )}
        </section>

        {/* Form */}
        <section className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Exploitation status</span>
            <select
              value={form.exploitationStatus}
              onChange={(e) =>
                setForm({
                  ...form,
                  exploitationStatus: e.target
                    .value as VulnerabilityExploitationStatus,
                })
              }
              aria-label="exploitation status"
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              {EXPLOIT_STATUSES.map((s) => (
                <option key={s} value={s} className="capitalize">
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.patchAvailable}
              onChange={(e) =>
                setForm({ ...form, patchAvailable: e.target.checked })
              }
              aria-label="patch available"
            />
            Patch available
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Admin notes</span>
            <textarea
              rows={6}
              value={form.adminNotes}
              onChange={(e) =>
                setForm({ ...form, adminNotes: e.target.value })
              }
              aria-label="admin notes"
              placeholder="Markdown supported. Surfaced on the CISO-facing detail page."
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          {/* V3 — Patch classifier overrides. Both fields are optional; saving
              an edit marks the CVE as admin-sourced so the classifier can't
              overwrite on its next run (newest-wins). */}
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Fix version hint</span>
            <input
              type="text"
              value={form.fixVersionHint}
              onChange={(e) =>
                setForm({ ...form, fixVersionHint: e.target.value })
              }
              aria-label="fix version hint"
              maxLength={200}
              placeholder='e.g. "Cisco IOS XE 17.9.1"'
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <span className="text-xs text-muted-foreground">
              Classifier-suggested; override when NVD lags the vendor advisory.
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">Primary advisory URL</span>
            <input
              type="url"
              value={form.primaryAdvisoryUrl}
              onChange={(e) =>
                setForm({ ...form, primaryAdvisoryUrl: e.target.value })
              }
              aria-label="primary advisory url"
              placeholder="https://vendor.com/security/advisory/..."
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <span className="text-xs text-muted-foreground">
              Canonical vendor advisory shown on the CISO Remediation card.
            </span>
          </label>
        </section>
      </div>

      {/* V5 — Vendor advisory admin surface. Flag-gated so the section stays
          hidden in prod until the adapters have backfilled. `cveId` drives the
          per-CVE fetch; `vulnId` is passed so mutations invalidate the public
          `['vulnerabilities', :id]` query alongside the admin list. */}
      {isV5AdvisoriesEnabled() && (
        <VendorAdvisoryAdminSection cveId={data.cveId} vulnId={data.id} />
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate('/admin/vulnerabilities')}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onSaveAndNext}
          disabled={update.isPending}
        >
          Save &amp; next unreviewed
        </Button>
        <Button type="button" onClick={onSave} disabled={update.isPending || !hasChanges}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </main>
  )
}
